import { useState } from "react";
import {
  Key,
  Copy,
  Check,
  Search,
  Filter,
  ToggleLeft,
  ToggleRight,
  Calendar,
  FileText,
  Building2,
  HelpCircle,
  Loader2,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AccessCode } from "../types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCodes, setCodeStatus } from "../services/codes";
import Notification, { NotificationAction } from "./Notification";
import Header from "./Header";

export default function Codes() {
  const { user, translations, token } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [codeToToggle, setCodeToToggle] = useState<AccessCode | null>(null);
  const [isUpdatingCode, setIsUpdatingCode] = useState(false);
  const {
    data: accessCodesRaw,
    isLoading,
    error,
  } = useQuery<AccessCode[]>({
    queryKey: ["accessCodes"],
    queryFn: () => getCodes(token!),
    enabled: !!token,
  });
  const queryClient = useQueryClient();
  const accessCodes = accessCodesRaw || [];
  const [notification, setNotification] = useState<{
    type: "success" | "error" | "warning" | "info";
    title: string;
    message?: string;
    actions?: NotificationAction[];
  } | null>(null);

  const filteredCodes = accessCodes.filter((code) => {
    const matchesSearch =
      code.surveyTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      code.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === "all" ||
      (filterStatus === "active" && code.isActive) ||
      (filterStatus === "inactive" && !code.isActive);
    const matchesRole =
      user?.role === "Administrator" ||
      code.organizationId === user?.organizationId;

    return matchesSearch && matchesFilter && matchesRole;
  });

  const copyToClipboard = async (code: string, codeId: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(codeId);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  const handleToggleClick = (code: AccessCode) => {
    // Validar: si el usuario es Organization y no es type 1 (entity), no permitir
    if (user?.role === "Organization" && code.type !== 1) {
      setNotification({
        type: "warning",
        title: translations.error || "Error",
        message: translations.genericSurveysCannotBeDisabledByOrganization || 
          "Las encuestas genéricas no pueden ser desactivadas por usuarios de organización",
      });
      return;
    }
    setCodeToToggle(code);
    setShowConfirmModal(true);
  };

  const toggleCodeStatus = async () => {
    if (!codeToToggle || isUpdatingCode) return;

    setIsUpdatingCode(true);
    try {
      const response = await setCodeStatus(token!, codeToToggle.id, !codeToToggle.isActive);
      if ("error" in response) {
        console.error(
          "Error actualizando estado del código:",
          response.message
        );

        setNotification({
          type: "error",
          title: translations.errorUpdatingSurveyStatus,
          message: response.message,
        });
        setIsUpdatingCode(false);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ["accessCodes"] });
      setNotification({
        type: "success",
        title: translations.surveyToggleStatusSuccessfully,
      });
      setShowConfirmModal(false);
      setCodeToToggle(null);
      setIsUpdatingCode(false);
    } catch (error) {
      setNotification({
        type: "error",
        title: translations.errorUpdatingSurveyStatus,
        message: (error as Error).message,
      });
      setIsUpdatingCode(false);
    }
  };

  const notificationComponent = notification && (
    <div
      role="alert"
      data-testid="notification"
      className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md"
    >
      <Notification
        type={notification.type}
        title={notification.title}
        actions={notification.actions}
        message={notification.message}
        duration={notification.actions?.length ? 0 : 5000}
        onClose={() => setNotification(null)}
      />
    </div>
  );
  if (isLoading) {
    return (
      <>
        {notificationComponent}
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {translations.accessCodes}
              </h1>
              <p className="text-gray-600 mt-1">
                {translations.loadingAccessCodes}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">
                {translations.loadingAccessCodes}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }
  if (error) {
    return (
      <>
        {notificationComponent}
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {translations.surveys}
              </h1>
              <p className="text-gray-600 mt-1">
                {translations.errorLoadingSurveys}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <FileText className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {translations.errorLoadingSurveys}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {error instanceof Error
                  ? error.message
                  : translations.unexpectedError}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {translations.retry}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {notificationComponent}
      
      {/* Modal de confirmación */}
      {showConfirmModal && codeToToggle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100">
                {codeToToggle.isActive ? (
                  <ToggleRight className="w-8 h-8 text-blue-600" />
                ) : (
                  <ToggleLeft className="w-8 h-8 text-green-600" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
                {codeToToggle.isActive 
                  ? translations.confirmDeactivation || "Confirmar desactivación"
                  : translations.confirmActivation || "Confirmar activación"}
              </h3>
              {isUpdatingCode ? (
                <div className="flex flex-col items-center justify-center mb-6">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
                  <p className="text-sm text-gray-600 text-center">
                    {translations.updatingCode || "Actualizando el código de acceso..."}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600 text-center mb-6">
                  {codeToToggle.isActive
                    ? translations.confirmDeactivateCodeMessage || 
                      "¿Estás seguro de que deseas desactivar este código de acceso?"
                    : translations.confirmActivateCodeMessage || 
                      "¿Estás seguro de que deseas activar este código de acceso?"}
                </p>
              )}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{translations.survey}:</span>
                    <span className="text-sm font-medium text-gray-900">{codeToToggle.surveyTitle}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{translations.code}:</span>
                    <span className="text-sm font-mono font-medium text-gray-900">{codeToToggle.code}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    if (!isUpdatingCode) {
                      setShowConfirmModal(false);
                      setCodeToToggle(null);
                    }
                  }}
                  disabled={isUpdatingCode}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {translations.cancel || "Cancelar"}
                </button>
                <button
                  onClick={toggleCodeStatus}
                  disabled={isUpdatingCode}
                  className={`px-4 py-2 text-white rounded-lg transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    codeToToggle.isActive
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {isUpdatingCode ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{translations.updatingCode || "Actualizando el código de acceso..."}</span>
                    </>
                  ) : (
                    <>
                      {codeToToggle.isActive
                        ? translations.deactivate || "Desactivar"
                        : translations.activate || "Activar"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="p-6 space-y-6">
        {/* Header */}
        <Header>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {translations.codes}
            </h1>
            <p className="text-gray-600 mt-1">
              {translations.manageSurveyCodes}
            </p>
          </div>
        </Header>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar códigos o encuestas..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filterStatus}
                onChange={(e) =>
                  setFilterStatus(e.target.value as "all" | "active" | "inactive")
                }
              >
                <option value="all">{translations.allStates}</option>
                <option value="active">{translations.active}</option>
                <option value="inactive">{translations.inactive}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Codes Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.survey}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.organization || "Organization"}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.type}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.code}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.questions || "Questions"}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.status}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.date}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.actions}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCodes.map((code) => (
                  <tr key={code.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {code.surveyTitle}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-700">
                        <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                        {code.organizationName || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        code.type === 1
                          ? "bg-green-100 text-green-800"
                          : "bg-blue-100 text-blue-800"
                      }`}>
                        {code.type === 1
                          ? translations.entity || "Específica"
                          : translations.generic || "Genérica"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                          {code.code}
                        </span>
                        <button
                          onClick={() => copyToClipboard(code.code, code.id)}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title={translations.copyCode}
                        >
                          {copiedCode === code.id ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-700">
                        <HelpCircle className="w-4 h-4 mr-2 text-blue-400" />
                        <span className="font-medium">
                          {code.questionCount ?? 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          code.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {code.isActive
                          ? translations.active
                          : translations.inactive}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(code.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {(() => {
                        // Si el usuario es Organization, solo mostrar el botón cuando type === 1 (entity/específica)
                        // Administrators pueden modificar todos los códigos
                        if (user?.role === "Organization" && code.type !== 1) {
                          return (
                            <span className="text-xs text-gray-400 italic">
                              {translations.readOnly || "Solo lectura"}
                            </span>
                          );
                        }
                        
                        return (
                          <button
                            onClick={() => handleToggleClick(code)}
                            className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm ${
                              code.isActive
                                ? "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 hover:shadow-md active:scale-95"
                                : "bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 hover:shadow-md active:scale-95"
                            }`}
                          >
                            {code.isActive ? (
                              <>
                                <ToggleRight className="w-5 h-5" />
                                <span>{translations.deactivate}</span>
                              </>
                            ) : (
                              <>
                                <ToggleLeft className="w-5 h-5" />
                                <span>{translations.activate}</span>
                              </>
                            )}
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredCodes.length === 0 && (
            <div className="text-center py-12">
              <Key className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {translations.noCodes}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {translations.codesGeneratedAutomatically}
              </p>
            </div>
          )}
        </div>

        {/* Code Rules Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Key className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                {translations.accessCodeRules}
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>{translations.onlyOneActiveCodePerSurvey}</li>
                  <li>{translations.codesGeneratedWhenCreatingSurvey}</li>
                  <li>{translations.canToggleCodesAsNeeded}</li>
                  <li>{translations.inactiveCodesDenyAccess}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
